import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { retry, takeUntil } from 'rxjs/operators';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { User } from 'app/models';
import { SnackBarService } from '../snackbar-service/snackbar.service';
import { SnackbarType } from 'app/constants/Enums';
import { ApiUrl } from 'app/constants/ApiUrls';
@Injectable({
  providedIn: 'root'
})
export class SelectedUserService implements OnDestroy {
  private _selectedUser: Subject<User> = new Subject<User>();
  public selectedUser$: Observable<User> = this._selectedUser.asObservable();

  private _error: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public error$: Observable<string> = this._error.asObservable();

  private onDestroy$ = new Subject<void>();

  constructor(
    private _http: HttpClient,
    private _translateService: TranslateService,
    public _snackbarService: SnackBarService
  ) {}

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  getSelectedUser(userId: string): void {
    let url = ApiUrl.usersV3 + `/${userId}`;

    this._http.get(url)
    .subscribe({
      next:(u: User) => {
        if (u) {
          if (!u.name) {
            u.name = `${u['firstName']} ${u['lastName']}`;
          }
          u.lastLoginString = u.lastLogin.toLocaleString();
        }
        this._selectedUser.next(u);
      },
     error: (error) => this._handleError(error)
  });
  }

  private _handleError(error: HttpErrorResponse) {
    let errorMsg: string;
    this._translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (errorMsg = value));
    if (error.error instanceof ErrorEvent) {
      console.error(`[selected-user-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[selected-user-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }
    this._error.next(errorMsg);
  }

  clearSelectedUser(): void {
    this._selectedUser.next(null);
    this._error.next(null);
  }

  verifyUserEmail(userId: string): void {
    this._http
      .patch(`${ApiUrl.usersV2}/${userId}`, { isEmailVerified: true })
      .pipe(retry(2))
      .subscribe(
        data => {
          let successMsg: string;
          this._translateService
            .get('USERS_SERVICE.success')
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(value => {
              successMsg = value;
              this._snackbarService.showSnackbar(
                SnackbarType.success,
                successMsg,
                successMsg
              );
            });

          this.getSelectedUser(userId);
        },
        error => this._handleVerifyEmailError(error)
      );
  }

  private _handleVerifyEmailError(error: HttpErrorResponse): void {
    let errorMsg: string;
    this._translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => {
        errorMsg = value;
        let errorTitle: string;
        this._translateService
              .get('SNACKBAR.error')
              .pipe(takeUntil(this.onDestroy$))
              .subscribe((value) => (errorTitle = value));
        this._snackbarService.showSnackbar(SnackbarType.error, errorTitle, errorMsg);
      });
    if (error.error instanceof ErrorEvent) {
      console.error(`[selected-user-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[selected-user-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }
  }
}
